import { useState } from "react";
import { useLocalPartial } from "../../util/local.ts";
import type { View } from "../../../../../common/types.ts";
import { MobileSheet } from "../../components/MobileSheet.tsx";
import { MobileStatusMetric } from "../../components/MobileStatusMetric.tsx";
import { MobileStatRow } from "../../mobile/mobilePrimitives.tsx";
import { helpers } from "../../util/helpers.ts";
import i18n from "../../util/i18n.ts";
import { formatPlayerName } from "../../util/playerNames.ts";
import { PositionText } from "../../util/positionNames.tsx";
import { getTeamFinanceRisk } from "../teamManagementDepth.ts";
import {
  FinancesForm,
  TeamFinancesMobileSummary,
  teamHypeLabel,
} from "../TeamFinances/index.tsx";
import { getTeamHypeStatus } from "../TeamFinances/financeStatusMetrics.ts";

export type TeamFinanceSheet = "overview" | "contracts" | "expenses" | "history";

type TeamFinanceData = NonNullable<View<"roster">["teamFinances"]>;

const FinanceOverview = ({ data }: { data: TeamFinanceData }) => {
  const current = data.barData[0];
  const hypeStatus = getTeamHypeStatus(current?.hype);
  const financeRisk = getTeamFinanceRisk({
    luxuryTaxAmount: data.luxuryTaxAmount,
    minPayrollAmount: data.minPayrollAmount,
    payroll: data.payroll,
    salaryCap: data.salaryCap,
    salaryCapType: data.salaryCapType,
  });

  return (
    <div data-roster-finance-overview="">
      <TeamFinancesMobileSummary
        contractTotals={data.contractTotals}
        financeRisk={financeRisk}
        luxuryPayroll={data.luxuryPayroll}
        luxuryTax={data.luxuryTax}
        luxuryTaxAmount={data.luxuryTaxAmount}
        minPayroll={data.minPayroll}
        minPayrollAmount={data.minPayrollAmount}
        payroll={data.payroll}
        salariesSeasons={data.salariesSeasons}
        salaryCap={data.salaryCap}
        salaryCapType={data.salaryCapType}
      />
      {current ? (
        <section className="mt-3" data-roster-finance-season-status="">
          <h2 className="h6 mb-2">
            {i18n.t("roster.financeSeasonSnapshot", {
              ns: "ui",
              defaultValue: "{{season}} season status",
              season: current.season,
            })}
          </h2>
          <div className="d-grid gap-2">
            <MobileStatRow
              label={i18n.t("teamFinances.wins", { ns: "ui", defaultValue: "Wins" })}
              value={current.won}
            />
            <MobileStatusMetric
              detail={`${Math.round(current.hype * 100)}%`}
              label={i18n.t("teamFinances.hype", { ns: "ui", defaultValue: "Hype" })}
              score={hypeStatus.score}
              value={teamHypeLabel(hypeStatus.state)}
            />
            <MobileStatRow
              label={i18n.t("teamFinances.avgAttendance", {
                ns: "ui",
                defaultValue: "Average attendance",
              })}
              value={helpers.numberWithCommas(Math.round(current.att))}
            />
          </div>
        </section>
      ) : null}
    </div>
  );
};

const PlayerContracts = ({ data }: { data: TeamFinanceData }) => (
  <div className="d-grid gap-2" data-roster-player-contracts="">
    {data.contracts.map((contract, index) => (
      <article
        className="border rounded-3 p-3"
        data-roster-player-contract={contract.pid}
        key={`${contract.pid}-${index}`}
      >
        <div className="d-flex align-items-start justify-content-between gap-3">
          <div>
            <a className="fw-semibold" href={helpers.leagueUrl(["player", contract.pid])}>
              {formatPlayerName(contract.firstName, contract.lastName)}
            </a>
            <div className="small text-body-secondary">
              <PositionText pos={contract.pos} /> ·{" "}
              {i18n.t("roster.financeContractCapShare", {
                ns: "ui",
                defaultValue: "{{value}}% of cap",
                value: contract.capPct.toFixed(1),
              })}
            </div>
          </div>
          {contract.released ? (
            <span className="badge text-bg-secondary">
              {i18n.t("teamFinances.releasedContracts", {
                ns: "ui",
                defaultValue: "Released contracts",
              })}
            </span>
          ) : null}
        </div>
        <div className="d-grid gap-1 mt-2">
          {data.salariesSeasons.map((season, seasonIndex) => (
            <div className="d-flex justify-content-between small" key={season}>
              <span className="text-body-secondary">{season}</span>
              <span className="fw-semibold">
                {contract.amounts[seasonIndex]
                  ? helpers.formatCurrency(contract.amounts[seasonIndex]!, "M")
                  : "—"}
              </span>
            </div>
          ))}
        </div>
      </article>
    ))}
  </div>
);

const ExpenseLevels = ({ data }: { data: TeamFinanceData }) => {
  const { gameSimInProgress } = useLocalPartial(["gameSimInProgress"]);

  if (!data.budget) {
    return (
      <p className="text-body-secondary mb-0" data-roster-expense-levels-disabled="">
        {i18n.t("teamFinances.budgetDisabledSimple", {
          ns: "ui",
          defaultValue: "Budget management is disabled in this league.",
        })}
      </p>
    );
  }

  return (
    <div data-roster-expense-levels="">
      <FinancesForm
        autoTicketPrice={data.autoTicketPrice}
        challengeNoRatings={data.challengeNoRatings}
        gameSimInProgress={gameSimInProgress}
        hideHeading
        otherTeamTicketPrices={data.otherTeamTicketPrices}
        phase={data.phase}
        salaryCap={data.salaryCap}
        spectator={data.spectator}
        t={data.t}
        tid={data.tid}
        userTid={data.userTid}
      />
    </div>
  );
};

const FinancialHistory = ({ data }: { data: TeamFinanceData }) => {
  const [allSeasonsOpen, setAllSeasonsOpen] = useState(false);
  const visibleRows = allSeasonsOpen ? data.barData : data.barData.slice(0, 3);
  const totalByPrefix = (row: TeamFinanceData["barData"][number], prefix: string) =>
    Object.entries(row).reduce(
      (total, [key, value]) =>
        key.startsWith(prefix) && typeof value === "number" ? total + value : total,
      0,
    ) / 1000;

  return data.barData.length > 0 ? (
    <div className="d-grid gap-2" data-roster-financial-history="">
      {visibleRows.map((row) => (
        <article className="border rounded-3 overflow-hidden" key={row.season}>
          <div className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom">
            <h2 className="h6 mb-0">{row.season}</h2>
            {row.champ ? (
              <span className="badge text-bg-warning">
                {i18n.t("roster.financeChampion", {
                  ns: "ui",
                  defaultValue: "Champion",
                })}
              </span>
            ) : null}
          </div>
          <MobileStatRow
            label={i18n.t("teamFinances.revenue", { ns: "ui", defaultValue: "Revenue" })}
            value={helpers.formatCurrency(totalByPrefix(row, "revenues"), "M")}
          />
          <MobileStatRow
            label={i18n.t("teamFinances.expenses", {
              ns: "ui",
              defaultValue: "Expenses",
            })}
            value={helpers.formatCurrency(totalByPrefix(row, "expenses"), "M")}
          />
          <MobileStatRow
            label={i18n.t("teamFinances.cash", { ns: "ui", defaultValue: "Cash" })}
            value={helpers.formatCurrency(row.cash, "M")}
          />
        </article>
      ))}
      {data.barData.length > 3 ? (
        <button
          aria-expanded={allSeasonsOpen}
          className="btn btn-link text-body-secondary"
          onClick={() => setAllSeasonsOpen((open) => !open)}
          type="button"
        >
          {allSeasonsOpen
            ? i18n.t("teamFinances.mobileCollapseYears", {
                ns: "ui",
                defaultValue: "Show fewer years",
              })
            : i18n.t("teamFinances.mobileExpandAllYears", {
                ns: "ui",
                defaultValue: "View all years",
              })}
        </button>
      ) : null}
    </div>
  ) : (
    <p className="text-body-secondary mb-0">
      {i18n.t("roster.financeHistoryEmpty", {
        ns: "ui",
        defaultValue: "No financial history is available yet.",
      })}
    </p>
  );
};

export const TeamFinanceSheets = ({
  active,
  data,
  onClose,
}: {
  active: TeamFinanceSheet | undefined;
  data: TeamFinanceData;
  onClose: () => void;
}) => {
  const title =
    active === "contracts"
      ? i18n.t("roster.playerContractsLink", {
          ns: "ui",
          defaultValue: "Player contracts",
        })
      : active === "expenses"
        ? i18n.t("roster.expenseLevelsLink", {
            ns: "ui",
            defaultValue: "Expense levels",
          })
        : active === "history"
          ? i18n.t("roster.financeHistoryLink", {
              ns: "ui",
              defaultValue: "Financial history",
            })
          : i18n.t("roster.teamFinancesLink", {
              ns: "ui",
              defaultValue: "Team finances",
            });

  return (
    <MobileSheet onClose={onClose} open={active !== undefined} title={title}>
      <div className="roster-team-finance-sheet" data-roster-team-finance-sheet={active}>
        {active === "overview" ? <FinanceOverview data={data} /> : null}
        {active === "contracts" ? <PlayerContracts data={data} /> : null}
        {active === "expenses" ? <ExpenseLevels data={data} /> : null}
        {active === "history" ? <FinancialHistory data={data} /> : null}
      </div>
    </MobileSheet>
  );
};
